import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Siren, RefreshCcw, Copy, ExternalLink } from 'lucide-react';

const DmarcBlocklistCard = ({ blocklistStats, blocklistLoading, fetchBlocklistStats, copyBlocklistUrl, apiOrigin }) => (
  <Card className="border-[#E5E7EB]" data-testid="dmarc-blocklist-card">
    <CardHeader className="pb-2">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <Siren className="w-4 h-4 text-[#FF3B30]" />DMARC Spoofer Blocklist Feed
        </CardTitle>
        <Button size="sm" variant="ghost" onClick={fetchBlocklistStats} disabled={blocklistLoading} data-testid="refresh-blocklist-stats">
          <RefreshCcw className={`w-3 h-3 mr-1 ${blocklistLoading ? 'animate-spin' : ''}`} />Refresh
        </Button>
      </div>
      <p className="text-[11px] text-[#9CA3AF] mt-1">
        Plain-text CIDR feed of IPs flagged as likely spoofers. Poll from your MTA/Cloudflare/fail2ban to auto-block at the edge.
      </p>
    </CardHeader>
    <CardContent className="space-y-3">
      <div className="flex items-center gap-2 p-2 rounded bg-[#F9FAFB] border border-[#E5E7EB]" data-testid="blocklist-url-row">
        <code className="text-[11px] flex-1 truncate text-[#4B5563]">
          GET {blocklistStats?.feed_url_path || '/api/email-config/dmarc-blocklist.txt'}
          {blocklistStats?.token_required ? '?token=<DMARC_BLOCKLIST_TOKEN>' : ''}
          &amp;days=30&amp;min_failing=5
        </code>
        <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={copyBlocklistUrl} data-testid="copy-blocklist-url">
          <Copy className="w-3 h-3 mr-1" />Copy URL
        </Button>
        <a
          href={`${apiOrigin}${blocklistStats?.feed_url_path || '/api/email-config/dmarc-blocklist.txt'}`}
          target="_blank" rel="noreferrer"
          className="text-[11px] text-[#002FA7] underline hover:no-underline inline-flex items-center gap-1"
          data-testid="open-blocklist-feed"
        >
          <ExternalLink className="w-3 h-3" />Open
        </a>
      </div>

      {blocklistStats ? (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="blocklist-counts">
          {[
            { key: 'last_24h', label: 'Last 24h' },
            { key: 'last_7d', label: 'Last 7 days' },
            { key: 'last_30d', label: 'Last 30 days' },
            { key: 'last_90d', label: 'Last 90 days' },
          ].map(b => (
            <div key={b.key} className="p-3 rounded-lg border border-[#E5E7EB] bg-white">
              <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide">{b.label}</p>
              <p className="text-lg font-semibold text-[#0A0A0A]" data-testid={`blocklist-count-${b.key}`}>
                {blocklistStats.counts?.[b.key] ?? 0}
              </p>
              <p className="text-[10px] text-[#9CA3AF]">distinct IPs</p>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-[11px] text-[#9CA3AF] italic">Loading blocklist stats…</p>
      )}

      {blocklistStats?.top_offenders_30d?.length > 0 && (
        <div data-testid="blocklist-top-offenders">
          <p className="text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wide mb-1">Top offenders (last 30 days)</p>
          <div className="space-y-1">
            {blocklistStats.top_offenders_30d.map((o, i) => (
              <div key={o.source_ip} className="flex items-center gap-2 p-2 rounded border border-[#E5E7EB] text-[11px]">
                <Badge className="bg-[#FF3B30]/10 text-[#FF3B30] text-[10px]">#{i + 1}</Badge>
                <code className="font-mono text-[#0A0A0A] flex-1">{o.source_ip}</code>
                <span className="text-[#6B7280]">{o.domain || '-'}</span>
                <Badge variant="secondary" className="text-[10px]">{o.max_failing} fails</Badge>
                <span className="text-[10px] text-[#9CA3AF]">{o.last_seen?.slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {blocklistStats && blocklistStats.counts?.last_90d === 0 && (
        <p className="text-[11px] text-[#9CA3AF] italic" data-testid="blocklist-empty-hint">
          Feed is empty. IPs are added automatically when DMARC aggregate reports show ≥5 failing messages from a single source IP in a single batch.
        </p>
      )}

      <details className="text-[11px] text-[#4B5563]">
        <summary className="cursor-pointer text-[#002FA7] hover:underline">How to consume this feed</summary>
        <div className="mt-2 pl-3 border-l-2 border-[#E5E7EB] space-y-2">
          <p><strong>Cloudflare WAF:</strong> Create an IP List, then use a scheduled Worker to pull this feed every 10min and <code className="bg-[#F3F4F6] px-1 rounded">{"PUT /accounts/{account_id}/rules/lists/{list_id}/items"}</code>.</p>
          <p><strong>fail2ban:</strong> Point an action at this URL with <code className="bg-[#F3F4F6] px-1 rounded">{"curl -s ... | grep -v '^#' | awk '{print $1}' | xargs -I{} fail2ban-client set <jail> banip {}"}</code>.</p>
          <p><strong>CrowdSec:</strong> Use the built-in <code className="bg-[#F3F4F6] px-1 rounded">url</code> blocklist parser with this endpoint.</p>
          <p className="text-[#9CA3AF]">Response supports <code className="bg-[#F3F4F6] px-1 rounded">If-None-Match</code> ETag for 304 responses — safe to poll every 1–5 minutes.</p>
        </div>
      </details>
    </CardContent>
  </Card>
);

export default DmarcBlocklistCard;
